import * as React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  FindAllCommunityEntitiesAdminQuery,
  QueryFindAllCommunityEntitiesAdminArgs,
  NewCommunityEntitySubscriptionSubscription,
} from "../../../../schemaTypes";
import { newCommunityEntitySubscription } from "../FindAll";

type SortBy = "name" | "shortName" | "entityType";
type CommunityEntity = FindAllCommunityEntitiesAdminQuery["findAllCommunityEntitiesAdmin"]["data"][number];

const SORT_FIELDS: SortBy[] = ["name", "shortName", "entityType"];

const DEFAULT_QUERY_VARIABLES: QueryFindAllCommunityEntitiesAdminArgs = {
  page: 1,
  pageSize: 10,
  entityTypeFilter: undefined,
  searchText: undefined,
  sortBy: "name",
  sortOrder: "asc",
};

const normalizeEntityTypeValue = (entityType: string) => entityType.trim().toLowerCase();
const normalizeText = (value?: string | null) => (value || "").trim().toLowerCase();
const normalizeSortBy = (sortBy?: string | null): SortBy => {
  return SORT_FIELDS.includes(sortBy as SortBy) ? (sortBy as SortBy) : "name";
};
const normalizeSortOrder = (sortOrder?: string | null) => {
  return sortOrder?.toLowerCase() === "desc" ? "desc" : "asc";
};
const normalizeEntityTypes = (entityType?: string[] | null) => {
  return Array.isArray(entityType) ? entityType.map(normalizeEntityTypeValue) : [];
};

const matchesFilters = (
  communityEntity: NewCommunityEntitySubscriptionSubscription["newCommunityEntity"],
  variables: QueryFindAllCommunityEntitiesAdminArgs,
) => {
  const normalizedSearchText = normalizeText(variables.searchText);
  const normalizedEntityTypeFilter = normalizeText(variables.entityTypeFilter);
  const normalizedEntityTypes = normalizeEntityTypes(communityEntity.entityType);

  if (
    normalizedEntityTypeFilter &&
    !normalizedEntityTypes.includes(normalizedEntityTypeFilter)
  ) {
    return false;
  }

  if (!normalizedSearchText) {
    return true;
  }

  return [
    normalizeText(communityEntity.name),
    normalizeText(communityEntity.shortName),
    ...normalizedEntityTypes,
  ].some((value) => value.includes(normalizedSearchText));
};

const getSortableValue = (
  communityEntity: CommunityEntity | NewCommunityEntitySubscriptionSubscription["newCommunityEntity"],
  sortBy?: string | null
) => {
  const currentSortBy = normalizeSortBy(sortBy);

  if (currentSortBy === "shortName") {
    return normalizeText(communityEntity.shortName);
  }

  if (currentSortBy === "entityType") {
    return normalizeEntityTypes(communityEntity.entityType).join(", ");
  }

  return normalizeText(communityEntity.name);
};

const sortCommunityEntities = (
  communityEntities: CommunityEntity[],
  sortBy?: string | null,
  sortOrder?: string | null,
) => {
  const currentSortOrder = normalizeSortOrder(sortOrder);

  return [...communityEntities].sort((a, b) => {
    const leftValue = getSortableValue(a, sortBy);
    const rightValue = getSortableValue(b, sortBy);
    const comparison = leftValue.localeCompare(rightValue, undefined, { sensitivity: "base" });

    if (comparison !== 0) {
      return currentSortOrder === "desc" ? -comparison : comparison;
    }

    return normalizeText(a.name).localeCompare(normalizeText(b.name), undefined, {
      sensitivity: "base"
    });
  });
};

export const findAllCommunityEntitiesAdminQuery = gql`
  query FindAllCommunityEntitiesAdmin(
    $page: Int!
    $pageSize: Int!
    $entityTypeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCommunityEntitiesAdmin(
      page: $page
      pageSize: $pageSize
      entityTypeFilter: $entityTypeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        name
        shortName
        entityType
        createdBy {
          id
          admin {
            id
            firstname
            lastname
            email
            level
          }
        }
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export function useFindAllCommunityEntitiesAdmin(
  variables?: Partial<QueryFindAllCommunityEntitiesAdminArgs>,
) {
  const queryVariables = React.useMemo(
    () => ({ ...DEFAULT_QUERY_VARIABLES, ...variables }),
    [variables],
  );

  const { data, loading, subscribeToMore } = useQuery<
    FindAllCommunityEntitiesAdminQuery,
    QueryFindAllCommunityEntitiesAdminArgs
  >(findAllCommunityEntitiesAdminQuery, {
    variables: queryVariables,
  });

  React.useEffect(() => {
    if (!subscribeToMore) return;

    const unsubscribe = subscribeToMore({
      document: newCommunityEntitySubscription,
      updateQuery: (
        prev,
        {
          subscriptionData,
        }: {
          subscriptionData: {
            data?: NewCommunityEntitySubscriptionSubscription;
          };
        },
      ) => {
        if (!subscriptionData.data) return prev;

        const newCommunityEntity = subscriptionData.data.newCommunityEntity;
        if (!matchesFilters(newCommunityEntity, queryVariables)) {
          return prev;
        }

        const existingResponse = prev.findAllCommunityEntitiesAdmin;
        const alreadyExists = existingResponse.data.some(
          (communityEntity) => communityEntity.id === newCommunityEntity.id
        );

        if (alreadyExists) {
          return prev;
        }

        const nextTotal = existingResponse.total + 1;
        const nextTotalPages = nextTotal === 0
          ? 0
          : Math.ceil(nextTotal / existingResponse.pageSize);
        const updatedData = existingResponse.page === 1
          ? sortCommunityEntities(
              [...existingResponse.data, newCommunityEntity as CommunityEntity],
              queryVariables.sortBy,
              queryVariables.sortOrder
            ).slice(0, existingResponse.pageSize)
          : existingResponse.data;

        return {
          ...prev,
          findAllCommunityEntitiesAdmin: {
            ...existingResponse,
            data: updatedData,
            total: nextTotal,
            totalPages: nextTotalPages
          }
        };
      },
    });

    return () => unsubscribe();
  }, [queryVariables, subscribeToMore]);

  const response = data?.findAllCommunityEntitiesAdmin;

  return {
    communityEntities: response?.data ?? [],
    total: response?.total ?? 0,
    page: response?.page ?? queryVariables.page,
    pageSize: response?.pageSize ?? queryVariables.pageSize,
    totalPages: response?.totalPages ?? 0,
    loading
  };
}
